import React from 'react';

function ColorPalette() {
  const colors = [
    {
      name: 'Primary Colors',
      shades: [
        { name: 'Primary Dark', hex: '#1f2661', desc: 'Deep Blue - Headers, Important Text' },
        { name: 'Primary', hex: '#2b3380', desc: 'ZOBOX Blue - Main Brand Color' },
        { name: 'Primary Light', hex: '#3d4aa3', desc: 'Light Blue - Hover States' },
        { name: 'Primary Lighter', hex: '#5c6bc0', desc: 'Lighter Blue - Backgrounds' },
      ]
    },
    {
      name: 'Accent Colors',
      shades: [
        { name: 'Accent Dark', hex: '#4d2c91', desc: 'Deep Purple - Secondary Actions' },
        { name: 'Accent', hex: '#7e57c2', desc: 'Purple - Accent Elements' },
        { name: 'Accent Light', hex: '#b085f5', desc: 'Light Purple - Highlights' },
      ]
    },
    {
      name: 'Status Colors',
      shades: [
        { name: 'Success', hex: '#4caf50', desc: 'Green - Success Messages, Active Status' },
        { name: 'Warning', hex: '#ff9800', desc: 'Orange - Warnings, Pending Items' },
        { name: 'Error', hex: '#f44336', desc: 'Red - Errors, Critical Alerts' },
        { name: 'Info', hex: '#2196f3', desc: 'Blue - Information, Links' },
      ]
    },
    {
      name: 'Neutral Colors',
      shades: [
        { name: 'Background', hex: '#f5f7fa', desc: 'Page Background' },
        { name: 'Surface', hex: '#ffffff', desc: 'Card Backgrounds' },
        { name: 'Border', hex: '#e0e0e0', desc: 'Borders, Dividers' },
        { name: 'Text Primary', hex: '#2c3e50', desc: 'Main Text' },
        { name: 'Text Secondary', hex: '#546e7a', desc: 'Secondary Text' },
        { name: 'Sidebar', hex: '#1a1f3a', desc: 'Sidebar Background' },
      ]
    },
  ];

  return (
    <div className="min-h-screen p-8" style={{backgroundColor: '#f5f7fa'}}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{color: '#2b3380'}}>ZOBOX Color Palette</h1>
          <p className="text-xl" style={{color: '#546e7a'}}>Based on your brand color #2b3380</p>
        </div>

        {colors.map((category, idx) => (
          <div key={idx} className="mb-12">
            <h2 className="text-2xl font-bold mb-6" style={{color: '#2c3e50'}}>{category.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.shades.map((shade, sIdx) => (
                <div 
                  key={sIdx} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden"
                  data-testid={`color-card-${shade.name.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div 
                    className="h-32 flex items-center justify-center"
                    style={{backgroundColor: shade.hex}}
                  >
                    <span className="text-white font-bold text-lg">{shade.hex}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2" style={{color: '#2c3e50'}}>{shade.name}</h3>
                    <p className="text-sm" style={{color: '#546e7a'}}>{shade.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Usage Examples */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6" style={{color: '#2c3e50'}}>Usage Examples</h2>
          
          {/* Buttons */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <h3 className="text-xl font-bold mb-4" style={{color: '#2c3e50'}}>Buttons</h3>
            <div className="flex flex-wrap gap-4">
              <button className="px-6 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#2b3380'}}>Primary Button</button>
              <button className="px-6 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#4caf50'}}>Success Button</button>
              <button className="px-6 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#ff9800'}}>Warning Button</button>
              <button className="px-6 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#f44336'}}>Error Button</button>
              <button className="px-6 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#7e57c2'}}>Accent Button</button>
            </div>
          </div>

          {/* Cards */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-xl font-bold mb-4" style={{color: '#2c3e50'}}>Status Cards</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-lg p-6 text-white" style={{backgroundColor: '#2b3380'}}>
                <h4 className="font-semibold mb-2">Primary Card</h4>
                <p className="text-3xl font-bold">100</p>
              </div>
              <div className="rounded-lg p-6 text-white" style={{backgroundColor: '#4caf50'}}>
                <h4 className="font-semibold mb-2">Success Card</h4>
                <p className="text-3xl font-bold">85%</p>
              </div>
              <div className="rounded-lg p-6 text-white" style={{backgroundColor: '#ff9800'}}>
                <h4 className="font-semibold mb-2">Warning Card</h4>
                <p className="text-3xl font-bold">12</p>
              </div>
              <div className="rounded-lg p-6 text-white" style={{backgroundColor: '#7e57c2'}}>
                <h4 className="font-semibold mb-2">Accent Card</h4>
                <p className="text-3xl font-bold">5</p>
              </div>
            </div>
          </div>
        </div>

        {/* Approval Section */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Ready to Approve?</h2>
          <p className="text-lg mb-6" style={{color: '#546e7a'}}>This color palette has been designed to match your brand color #2b3380</p>
          <div className="flex justify-center gap-4">
            <button className="px-8 py-3 rounded-lg text-white font-medium" style={{backgroundColor: '#4caf50'}}>✓ Approve Colors</button>
            <button className="px-8 py-3 rounded-lg font-medium" style={{backgroundColor: '#e0e0e0', color: '#2c3e50'}}>Request Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ColorPalette;
